import { supabase } from "../../../lib/supabase";
import { mapMessageType, isFromMe } from "../sync";
import { MappedMessage } from "./historyMessageMapper";
import { isTemporaryRemoteMediaUrl } from "../../../utils/failedImageCache";

/**
 * Persiste mensagens mapeadas e deduplicadas no banco de dados.
 * Remove fallbacks sintéticos conflitantes e atualiza metadados do Chat.
 *
 * Princípios: Single Responsibility (Isolamento de Infraestrutura/DB).
 */
export async function persistMappedMessages(
  mapped: MappedMessage[],
  conversationDbId: string,
  organizationId: string,
  ownerJid: string,
  remoteJid: string,
): Promise<number> {
  let insertedCount = 0;

  // 1. Resolve mensagens já existentes em lotes para dedup
  const messageIds = mapped.map((m) => m.messageid).filter(Boolean);
  const existingMsgIds = new Set<string>();

  for (let b = 0; b < messageIds.length; b += 100) {
    const batch = messageIds.slice(b, b + 100);
    const { data: existing } = await supabase
      .from("chat_messages")
      .select("message_id")
      .in("message_id", batch)
      .eq("conversation_id", conversationDbId);
    if (existing) existing.forEach((m) => existingMsgIds.add(m.message_id));
  }

  // 2. Remove sintéticas duplicadas criadas por fallbacks prévios
  const { data: existingSynthetics } = await supabase
    .from("chat_messages")
    .select("message_id, content, timestamp")
    .eq("conversation_id", conversationDbId)
    .like("message_id", "sync_%");

  if (existingSynthetics?.length) {
    const syntheticsToDelete: string[] = [];
    for (const synth of existingSynthetics) {
      const hasRealEquivalent = mapped.some(
        (m) =>
          m.text === synth.content &&
          Math.abs(
            m.messageTimestamp - new Date(synth.timestamp).getTime() / 1000,
          ) < 5,
      );
      if (hasRealEquivalent) {
        syntheticsToDelete.push(synth.message_id);
      }
    }
    if (syntheticsToDelete.length > 0) {
      await supabase
        .from("chat_messages")
        .delete()
        .in("message_id", syntheticsToDelete)
        .eq("conversation_id", conversationDbId);
    }
  }

  // 3. Filtra as mensagens finais que são novas
  const newMessages = mapped.filter((m) => !existingMsgIds.has(m.messageid));

  // 4. Insere no banco
  if (newMessages.length > 0) {
    const messagesToInsert = newMessages.map((msg) => {
      const msgTs =
        msg.messageTimestamp > 9999999999
          ? new Date(msg.messageTimestamp).toISOString()
          : new Date(msg.messageTimestamp * 1000).toISOString();
      return {
        message_id: msg.messageid,
        conversation_id: conversationDbId,
        sender: isFromMe(msg as any, ownerJid) ? "me" : "them",
        content: msg.text || "",
        message_type: mapMessageType(msg.messageType),
        media_url: msg.fileURL && !isTemporaryRemoteMediaUrl(msg.fileURL) ? msg.fileURL : null,
        status: "delivered",
        organization_id: organizationId,
        timestamp: msgTs,
        quoted_message_id: msg.quoted || null,
      };
    });

    for (let b = 0; b < messagesToInsert.length; b += 50) {
      const batch = messagesToInsert.slice(b, b + 50);
      const { error: insertError } = await supabase
        .from("chat_messages")
        .upsert(batch, {
          onConflict: "conversation_id,message_id",
          ignoreDuplicates: true,
        });
      if (!insertError) insertedCount += batch.length;
      else
        console.warn(
          `[History Sync] ⚠️ Erro lote ${remoteJid}:`,
          insertError.message,
        );
    }
  }

  // 5. Atualiza `last_message` da conversa
  const latestMsg = mapped.reduce((newest, msg) => {
    return (msg.messageTimestamp || 0) > (newest.messageTimestamp || 0)
      ? msg
      : newest;
  }, mapped[0]);

  if (latestMsg) {
    const latestTs =
      latestMsg.messageTimestamp > 9999999999
        ? new Date(latestMsg.messageTimestamp).toISOString()
        : new Date(latestMsg.messageTimestamp * 1000).toISOString();
    await supabase
      .from("chat_conversations")
      .update({
        last_message:
          latestMsg.text || `[${mapMessageType(latestMsg.messageType)}]`,
        last_message_time: latestTs,
        updated_at: latestTs,
      })
      .eq("id", conversationDbId);
  }

  return insertedCount;
}
